import { useState, useRef, useEffect } from "react";
import { Send, MessageSquare, Loader2, Mic, MicOff, X, Bot, User, LifeBuoy } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const salesQuickActions = [
  { label: "Our Robots", message: "Tell me about your robots" },
  { label: "Book Demo", message: "I want to book a demo" },
  { label: "Pricing", message: "What are your prices?" },
];

const supportQuickActions = [
  { label: "Robot won't start", message: "My robot won't start" },
  { label: "WiFi issue", message: "My robot is offline / WiFi issue" },
  { label: "Voice agent not answering", message: "My voice agent isn't answering calls" },
];

const FloatingChatWidget = () => {
  const location = useLocation();
  const onSupportRoute = location.pathname.startsWith("/support") || location.pathname.startsWith("/portal/support");
  const [isOpen, setIsOpen] = useState(false);
  const [supportMode, setSupportMode] = useState(onSupportRoute);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [showBadge, setShowBadge] = useState(true);
  const chatRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);

  // Auto-toggle Support mode based on route
  useEffect(() => {
    setSupportMode(onSupportRoute);
  }, [onSupportRoute]);

  // Listen for external requests to open chat in support mode
  useEffect(() => {
    const handler = () => {
      setSupportMode(true);
      setIsOpen(true);
      setShowBadge(false);
    };
    window.addEventListener("open-support-chat", handler as EventListener);
    return () => window.removeEventListener("open-support-chat", handler as EventListener);
  }, []);

  const quickActions = supportMode ? supportQuickActions : salesQuickActions;

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)) {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;

      recognitionRef.current.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setInput(prev => prev + (prev ? ' ' : '') + transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event: any) => {
        if (import.meta.env.DEV) {
          console.error('Speech recognition error:', event.error);
        }
        setIsListening(false);
        if (event.error === 'not-allowed') {
          toast.error('Microphone access denied. Please enable microphone permissions.');
        }
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, []);

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setShowBadge(false);
    }
  };

  const toggleVoiceInput = () => {
    if (!recognitionRef.current) {
      toast.error('Voice input is not supported on this browser.');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
        toast.info('Listening... Speak now');
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Error starting speech recognition:', error);
        }
      }
    }
  };

  const handleSend = async (messageText?: string) => {
    const message = (messageText || input).trim();
    if (!message || isLoading) return;

    if (message.length > 2000) {
      toast.error("Message is too long. Please keep it under 2000 characters.");
      return;
    }

    const userMessage: Message = { role: 'user', content: message };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('ai-chat', {
        body: { messages: newMessages.slice(-12), mode: supportMode ? 'support' : 'sales' }
      });

      if (error) {
        if (import.meta.env.DEV) console.error("AI chat error:", error);
        throw error;
      }
      if ((data as any)?.error) {
        throw new Error((data as any).error);
      }

      const answer = data?.answer || 'Sorry, I could not generate a response. Please call (336) 203-3184 for immediate help.';
      const assistantMessage: Message = { role: 'assistant', content: answer };
      setMessages([...newMessages, assistantMessage]);

    } catch (error: any) {
      if (import.meta.env.DEV) console.error('AI chat error:', error);
      const msg = error?.message || "Unable to reach the assistant. Please try again.";
      toast.error(msg);
      setMessages([...newMessages, { role: 'assistant', content: "I'm having trouble connecting right now. Please call (336) 203-3184 or email support@neilai.ai." }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickAction = (message: string) => {
    setInput(message);
    handleSend(message);
  };

  return (
    <div className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] right-4 sm:bottom-[calc(1.5rem+env(safe-area-inset-bottom))] sm:right-6 z-[9999] font-sans">
      {/* Chat Toggle Button */}
      <button
        onClick={toggleChat}
        className={cn(
          "w-14 h-14 rounded-full flex items-center justify-center text-white cursor-pointer transition-all duration-300 shadow-lg hover:scale-110",
          "bg-gradient-to-br from-primary to-accent",
          isOpen && "rotate-0"
        )}
        aria-label="Toggle chat"
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <MessageSquare className="w-6 h-6" />
        )}
        {showBadge && !isOpen && (
          <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold">
            1
          </span>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed sm:absolute bottom-20 sm:bottom-16 right-4 sm:right-0 left-4 sm:left-auto w-auto sm:w-[360px] h-[min(550px,calc(100vh-7rem))] bg-background rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300 border border-border">
          {/* Header */}
          <div className={cn("p-4 flex items-center justify-between bg-gradient-to-r", supportMode ? "from-[#FFD700] to-[#FFA500]" : "from-primary to-accent")}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-background rounded-full flex items-center justify-center">
                {supportMode ? <LifeBuoy className="w-5 h-5 text-[#FFA500]" /> : <Bot className="w-5 h-5 text-primary" />}
              </div>
              <div>
                <h3 className={cn("font-bold text-sm", supportMode ? "text-[#05070A]" : "text-primary-foreground")}>{supportMode ? "Neil AI Support Assistant" : "Neil AI Assistant"}</h3>
                <p className={cn("text-xs", supportMode ? "text-[#05070A]/80" : "text-primary-foreground/80")}>{supportMode ? "24/7 · (336) 203-3184" : "Online • Replies instantly"}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setSupportMode(!supportMode)}
                className={cn("text-[10px] px-2 py-1 rounded-full font-semibold transition-colors", supportMode ? "bg-[#05070A]/10 text-[#05070A] hover:bg-[#05070A]/20" : "bg-white/15 text-primary-foreground hover:bg-white/25")}
                title="Toggle Support mode"
              >
                {supportMode ? "Sales mode" : "Support mode"}
              </button>
              <button
                onClick={toggleChat}
                className={cn("w-8 h-8 rounded-full flex items-center justify-center transition-colors", supportMode ? "hover:bg-[#05070A]/10" : "hover:bg-white/10")}
                aria-label="Close chat"
              >
                <X className={cn("w-5 h-5", supportMode ? "text-[#05070A]" : "text-primary-foreground")} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={chatRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/30">
            {messages.length === 0 && (
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-primary-foreground" />
                  </div>
                  <div className="bg-background p-3 rounded-xl shadow-sm max-w-[80%]">
                    {supportMode ? (
                      <>
                        <p className="text-sm mb-2">Hi! I'm the Neil AI Support Assistant. I can help with:</p>
                        <ul className="text-sm space-y-1 text-muted-foreground list-disc pl-4">
                          <li>Robot troubleshooting (CC1, MT1, BellaBot, etc.)</li>
                          <li>AI Employees, voice agents, CRM issues</li>
                          <li>Opening a ticket or service request</li>
                          <li>Routing you to a human technician 24/7</li>
                        </ul>
                        <p className="text-sm mt-2"><strong>What issue are you experiencing today?</strong></p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm mb-2">Hi! I'm Neil, your AI assistant. I can help you with:</p>
                        <ul className="text-sm space-y-1 text-muted-foreground list-disc pl-4">
                          <li>Robot product information</li>
                          <li>Scheduling demos</li>
                          <li>Business automation solutions</li>
                          <li>Contact & pricing</li>
                        </ul>
                        <p className="text-sm mt-2">What can I help you with today?</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={cn("flex gap-3", msg.role === 'user' && "flex-row-reverse")}
              >
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                  msg.role === 'user' 
                    ? "bg-secondary" 
                    : "bg-gradient-to-br from-primary to-accent"
                )}>
                  {msg.role === 'user' ? (
                    <User className="w-4 h-4 text-secondary-foreground" />
                  ) : (
                    <Bot className="w-4 h-4 text-primary-foreground" />
                  )}
                </div>
                <div className={cn(
                  "p-3 rounded-xl max-w-[75%] shadow-sm",
                  msg.role === 'user' 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-background"
                )}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-primary-foreground" />
                </div>
                <div className="bg-background p-3 rounded-xl shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="p-3 bg-background border-t border-border">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder={isListening ? "Listening..." : "Type your message..."}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                disabled={isLoading}
                className="flex-1 rounded-full border-muted"
                aria-label="Chat message input"
              />
              <Button
                onClick={toggleVoiceInput}
                disabled={isLoading}
                size="icon"
                variant={isListening ? "default" : "outline"}
                className="rounded-full w-10 h-10 flex-shrink-0"
                aria-label="Voice input"
              >
                {isListening ? (
                  <MicOff className="w-4 h-4 animate-pulse" />
                ) : (
                  <Mic className="w-4 h-4" />
                )}
              </Button>
              <Button
                onClick={() => handleSend()}
                disabled={isLoading || !input.trim()}
                size="icon"
                className="rounded-full w-10 h-10 flex-shrink-0 bg-gradient-to-r from-primary to-accent hover:opacity-90"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>

            {/* Quick Actions */}
            {messages.length === 0 && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {quickActions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuickAction(action.message)}
                    className="px-3 py-1.5 bg-muted hover:bg-muted/80 rounded-full text-xs font-medium transition-colors"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingChatWidget;
